import axios, { type AxiosInstance } from 'axios';
import { onlineManager } from '@tanstack/react-query';
import qs from 'qs';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.erpgold.vn/v1';
const ACCESS_TOKEN_KEY = 'erp_access_token';
const REFRESH_TOKEN_KEY = 'erp_refresh_token';

let apiUnavailable = false;

export class ApiUnavailableError extends Error {
  code = 'API_UNAVAILABLE';
  isApiUnavailableError = true;

  constructor(message = 'API server is unavailable') {
    super(message);
    this.name = 'ApiUnavailableError';
  }
}

export function isApiUnavailableError(error: unknown): error is ApiUnavailableError {
  return Boolean(
    error &&
      typeof error === 'object' &&
      (error as { isApiUnavailableError?: unknown }).isApiUnavailableError,
  );
}

function markApiUnavailable(): void {
  if (!apiUnavailable) {
    apiUnavailable = true;
    onlineManager.setOnline(false);
  }
}

function resumeApiAvailability(): void {
  if (apiUnavailable) {
    apiUnavailable = false;
    onlineManager.setOnline(true);
  }
}

function isNetworkUnavailableError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response) return false;
  return (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNABORTED' ||
    error.message === 'Network Error'
  );
}

function attachAvailabilityInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    if (apiUnavailable) {
      return Promise.reject(new ApiUnavailableError());
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      resumeApiAvailability();
      return response;
    },
    (error) => {
      if (isNetworkUnavailableError(error)) {
        markApiUnavailable();
        return Promise.reject(new ApiUnavailableError(error.message));
      }
      return Promise.reject(error);
    },
  );
}

if (typeof window !== 'undefined') {
  window.addEventListener('focus', resumeApiAvailability);
  window.addEventListener('online', resumeApiAvailability);
}

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  paramsSerializer: (params) =>
    qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true }),
});
attachAvailabilityInterceptors(api);

// Shared refresh promise (in-tab) — fallback cho browser không hỗ trợ Web Locks API.
// Web Locks API là cơ chế chính để sync giữa nhiều tab cùng origin (ngăn race condition
// đa tab triggers BE REFRESH_REUSE_DETECTED revoke chain — ADR-012 §D7).
let _refreshPromise: Promise<string> | null = null;

const REFRESH_LOCK_NAME = 'erp-auth-refresh';

async function postRefresh(refreshToken: string): Promise<string> {
  // Bare axios — tránh recursive interceptor nếu /auth/refresh tự trả 401.
  // BE response (ADR-013 D2): { data: { access_token, refresh_token, ... }, success: true }
  const res = await axios.post(
    `${API_URL}/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );
  const payload = res.data?.data ?? res.data ?? {};
  const accessToken: string | undefined = payload.access_token;
  const newRefreshToken: string | undefined = payload.refresh_token;

  if (!accessToken) throw new Error('REFRESH_MISSING_TOKEN');

  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (newRefreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

  return accessToken;
}

/**
 * Refresh access token, sync giữa các tab qua Web Locks API.
 *
 * `accessAtRequest` = access token gắn vào request 401 ban đầu. Khi vào lock, kiểm tra
 * localStorage: nếu access đã đổi (do tab khác refresh xong trước) → dùng luôn, không
 * gọi /auth/refresh thêm lần nữa (tránh thừa request và race với BE rotation).
 *
 * Fallback in-tab `_refreshPromise` cho browser không support Web Locks (Safari <15.4).
 */
async function doRefresh(accessAtRequest: string | null): Promise<string> {
  const refreshOnce = async (): Promise<string> => {
    const latestAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (latestAccess && latestAccess !== accessAtRequest) {
      // Tab khác đã refresh xong trong lúc tab này chờ lock — dùng access mới luôn.
      return latestAccess;
    }
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error('NO_REFRESH_TOKEN');
    return postRefresh(refreshToken);
  };

  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, refreshOnce);
  }
  // Fallback: in-tab dedup. Mất guarantee multi-tab — Safari < 15.4.
  if (!_refreshPromise) {
    _refreshPromise = refreshOnce().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // EG-369: owner/manager mở ca POS với store_id chọn → persist sessionStorage
    // → mọi request /pos/* tự inject `?store_id` để BE scope theo store. Cashier
    // có JWT.sid thì BE resolve từ token, FE không inject (tránh ghi đè).
    // Naming `store_id` (snake_case) theo ADR-013 — query string snake_case.
    const url = String(config.url ?? '');
    if (url.startsWith('/pos') || url.includes('/pos/')) {
      try {
        const shiftStoreId = sessionStorage.getItem('pos.shiftStoreId');
        if (shiftStoreId) {
          config.params = { ...(config.params ?? {}), store_id: shiftStoreId };
        }
      } catch {
        /* sessionStorage unavailable (SSR / private mode) */
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const authHeader: string | undefined =
          originalRequest.headers?.Authorization;
        const accessAtRequest = authHeader?.startsWith('Bearer ')
          ? authHeader.slice('Bearer '.length)
          : null;
        const accessToken = await doRefresh(accessAtRequest);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        window.location.href = '/auth/login';
        return Promise.reject(error);
      }
    }

    if (error.response?.data?.error?.message) {
      error.message = error.response.data.error.message;
    }

    return Promise.reject(error);
  }
);

const noAuthApi = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});
attachAvailabilityInterceptors(noAuthApi);

export default api;
export { noAuthApi };
